import axios from "axios";

const API_BASE = window._env_!.REACT_APP_API_BASE!;

/**
 * Fetches the cart data from the API.
 *
 * If a `cartId` is provided, retrieves the specific cart with that ID.
 * Otherwise, retrieves the current cart.
 *
 * @param cartId - (Optional) The ID of the cart to fetch.
 * @returns A promise that resolves to the cart data.
 */
export const fetchCart = async (cartId?: string) => {
  const url = cartId ? `${API_BASE}/cart/${cartId}` : `${API_BASE}/cart`;
  const response = await axios.get(url);
  return response.data;
};

/**
 * Adds an item to the cart for a guest user.
 *
 * Sends a POST request to the API to add a specified quantity of a product to the cart.
 *
 * @param cartId - The unique identifier of the cart.
 * @param productId - The unique identifier of the product to add.
 * @param quantity - The number of units of the product to add to the cart.
 * @returns A promise resolving to the response of the POST request.
 */
export const addCartItem = async (cartId: string, productId: string, quantity: number) => {
  return axios.post(`${API_BASE}/cart/${cartId}/add`, { productId, quantity });
};

/**
 * Removes specified items from a cart by sending a DELETE request to the API.
 *
 * @param cartId - The unique identifier of the cart from which items will be removed.
 * @param productIds - An array of product IDs to be removed from the cart.
 * @returns A promise resolving to the response of the DELETE request.
 */
export const removeCartItems = async (cartId: string, productIds: string[]) => {
  const params = new URLSearchParams();
  productIds.forEach(id => params.append("ids", id));
  return axios.delete(`${API_BASE}/cart/${cartId}/remove?${params.toString()}`);
};

/**
 * Fetches wishlist data from the API.
 *
 * If a `wishlistId` is provided, fetches the specific wishlist with that ID.
 * Otherwise, fetches all wishlists.
 *
 * @param wishlistId - (Optional) The ID of the wishlist to fetch.
 * @returns A promise that resolves to the wishlist data.
 */
export const fetchWishlist = async (wishlistId?: string) => {
  const url = wishlistId ? `${API_BASE}/wishlist/${wishlistId}` : `${API_BASE}/wishlist`;
  const response = await axios.get(url);
  return response.data;
};

/**
 * Adds a product to a wishlist by sending a POST request to the API.
 *
 * @param wishlistId - The unique identifier of the wishlist to which the product will be added.
 * @param productId - The unique identifier of the product to add to the wishlist.
 * @returns A promise that resolves with the response from the API.
 */
export const addWishlistItem = async (wishlistId: string, productId: string) => {
  return axios.post(`${API_BASE}/wishlist/${wishlistId}`, { productId });
};

/**
 * Removes one or more items from a wishlist by their product IDs.
 *
 * @param wishlistId - The unique identifier of the wishlist.
 * @param productIds - An array of product IDs to be removed from the wishlist.
 * @returns A promise resolving to the Axios response of the DELETE request.
 */
export const removeWishlistItems = async (wishlistId: string, productIds: string[]) => {
  const params = new URLSearchParams();
  productIds.forEach(id => params.append("ids", id));
  return axios.delete(`${API_BASE}/wishlist/${wishlistId}?${params.toString()}`);
};

/**
 * Fetches the complete details of a cart by its ID.
 *
 * @param cartId - The unique identifier of the cart to retrieve.
 * @returns A promise that resolves to the full cart data from the API.
 */
export const fetchFullCart = async (cartId: string) => {
  return axios.get(`${API_BASE}/cart/${cartId}/full`);
};

/**
 * Fetches the list of pickup stores from the configured API endpoint.
 *
 * Makes a GET request to the pickup store API using the provided authorization header.
 * Filters the returned data to include only stores where `pickup_store` equals 1.
 *
 * @returns {Promise<any[]>} A promise that resolves to an array of pickup store objects.
 * @throws {Error} If the API response indicates failure (`json.success` is false).
 */
export const fetchPickupStores = async (): Promise<any[]> => {
  const response = await fetch(window._env_!.REACT_APP_PICKUP_STORE_API!, {
    headers: {
      Authorization: window._env_!.REACT_APP_PICKUP_STORE_AUTH!,
    },
  });

  const json = await response.json();
  if (!json.success) throw new Error("Pickup-Stores konnten nicht geladen werden.");
  return json.data.filter((store: any) => store.pickup_store === 1);
};